import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum DamageLocation {
  FrontBumper = 'FrontBumper',
  Hood = 'Hood',
  FrontWindshield = 'FrontWindshield',
  Roof = 'Roof',
  RearWindshield = 'RearWindshield',
  TrunkTailgate = 'TrunkTailgate',
  RearBumper = 'RearBumper',
  LeftFrontDoor = 'LeftFrontDoor',
  LeftRearDoor = 'LeftRearDoor',
  LeftSide = 'LeftSide',
  RightFrontDoor = 'RightFrontDoor',
  RightRearDoor = 'RightRearDoor',
  RightSide = 'RightSide',
  Undercarriage = 'Undercarriage',
  Other = 'Other'
}

export const DamageLocationLabels: Record<DamageLocation, string> = {
  [DamageLocation.FrontBumper]: 'Front Bumper',
  [DamageLocation.Hood]: 'Hood',
  [DamageLocation.FrontWindshield]: 'Front Windshield',
  [DamageLocation.Roof]: 'Roof',
  [DamageLocation.RearWindshield]: 'Rear Windshield',
  [DamageLocation.TrunkTailgate]: 'Trunk/Tailgate',
  [DamageLocation.RearBumper]: 'Rear Bumper',
  [DamageLocation.LeftFrontDoor]: 'Left Front Door',
  [DamageLocation.LeftRearDoor]: 'Left Rear Door',
  [DamageLocation.LeftSide]: 'Left Side',
  [DamageLocation.RightFrontDoor]: 'Right Front Door',
  [DamageLocation.RightRearDoor]: 'Right Rear Door',
  [DamageLocation.RightSide]: 'Right Side',
  [DamageLocation.Undercarriage]: 'Undercarriage',
  [DamageLocation.Other]: 'Other'
};

interface DamageLocationsState {
  selectedLocations: DamageLocation[];
  noDamage: boolean;
}

const initialState: DamageLocationsState = {
  selectedLocations: [],
  noDamage: false
};

const damageLocationsSlice = createSlice({
  name: 'damageLocations',
  initialState,
  reducers: {
    toggleDamageLocation: (state, action: PayloadAction<DamageLocation>) => {
      const location = action.payload;
      const index = state.selectedLocations.indexOf(location);
      
      if (index > -1) {
        state.selectedLocations.splice(index, 1);
      } else {
        state.selectedLocations.push(location);
        state.noDamage = false;
      }
    },
    setDamageLocations: (state, action: PayloadAction<DamageLocation[]>) => {
      state.selectedLocations = action.payload;
      state.noDamage = action.payload.length === 0;
    },
    clearDamageLocations: (state) => {
      state.selectedLocations = [];
      state.noDamage = false;
    },
    setNoDamage: (state, action: PayloadAction<boolean>) => {
      state.noDamage = action.payload;
      if (action.payload) {
        state.selectedLocations = [];
      }
    }
  }
});

export const {
  toggleDamageLocation,
  setDamageLocations,
  clearDamageLocations,
  setNoDamage
} = damageLocationsSlice.actions;

export default damageLocationsSlice.reducer;

// Helper functions
export const isFrontDamage = (locations: DamageLocation[]): boolean => {
  const frontLocations = [
    DamageLocation.FrontBumper,
    DamageLocation.Hood,
    DamageLocation.FrontWindshield
  ];
  return locations.some(location => frontLocations.includes(location));
};

export const isRearDamage = (locations: DamageLocation[]): boolean => {
  const rearLocations = [
    DamageLocation.RearBumper,
    DamageLocation.TrunkTailgate,
    DamageLocation.RearWindshield
  ];
  return locations.some(location => rearLocations.includes(location));
};

export const isSideDamage = (locations: DamageLocation[]): boolean => {
  const sideLocations = [
    DamageLocation.LeftFrontDoor,
    DamageLocation.LeftRearDoor,
    DamageLocation.LeftSide,
    DamageLocation.RightFrontDoor,
    DamageLocation.RightRearDoor,
    DamageLocation.RightSide
  ];
  return locations.some(location => sideLocations.includes(location));
};